import React, { useState, useEffect, useRef } from 'react';
import { map } from 'lodash';
import MarkDown from 'react-markdown';

import { MembershipCardStatusEnum } from '../../../../jsxctl/utilctl';
import { AlreadyMember, MembershipCardConditionPopup} from '../memberships';
import type { MembershipTypeSaleInfoModel } from '../../../tsrestapi/models/membershiptypesaleinfomodel';
import { PriceGroupButtons } from './pricegroupbuttons';
import { analyseAddToCartErrorMessage } from '../../../logic';

// Utils
import TranslateManager from 'ts-translate-js';
import { AddMembershipCardToCartResult, PriceChangeSuggestion } from '../../../mvcapi/models/buyticket';
import { useAddTicketsToCartStore } from '../../../stores';
import { MembershipCardStatusEnumType } from '../../../logic/models/membership';
const tr = TranslateManager.boundTR;

interface MembershipCardPickupControlProps {
    membershipTypeInfos: MembershipTypeSaleInfoModel[];
    showAlreadyMemberSection: boolean;
    refreshActiveMembershipCards: (membershipTypeId: string, status: MembershipCardStatusEnumType, priceChangeSuggestions: PriceChangeSuggestion[]) => void;
    onContentChange: () => void;
}

export function MembershipCardPickupControl({membershipTypeInfos, showAlreadyMemberSection, refreshActiveMembershipCards, onContentChange}: MembershipCardPickupControlProps) {
    const [addMembershipCardProcessing, setAddMembershipCardProcessing] = useState(false);
    const [showPopupMessageCreditCardCondition, setShowPopupMessageCreditCardCondition] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedPriceGroup, setSelectedPriceGroup] = useState(null);
    const [eventDateID, setEventDateID] = useState(null);
    const getAddToCartRequest = useAddTicketsToCartStore(s => s.getAddToCartRequest);

  const container = useRef();
    useEffect(() => {  
        if(onContentChange != null) onContentChange();
    }, [onContentChange]);

    const handlePopupConfirmClose = () => {
        setShowPopupMessageCreditCardCondition(false);
    };

    const addMemberShipCardToCart = async (eventDateID, priceGroupID) => {
        setShowErrorMessage(false);
        setAddMembershipCardProcessing(true);
        setShowPopupMessageCreditCardCondition(false);
        
        // eslint-disable-next-line semi
        const data: AddMembershipCardToCartResult = await tsapp.mvcapi.postCtl("buyticket/addmembershipcardtocart", { 
            eventDateID: eventDateID,
            priceGroupID: priceGroupID,
            desiredTicketQty: 1,
            resetTemporarySelectedTickets: false,
            addToCartRequest: getAddToCartRequest() // For price change suggestion
        });

        if(data.IsSuccess) {
            if(typeof window.RefreshCartIFrame != 'undefined') 
                // We need to disable this rule because the function is a global one
                //eslint-disable-next-line sonarjs/new-cap
                window.RefreshCartIFrame(); // This is a global site function. We will have to do something for integrations that don't have a small cart!
            
            if(typeof window.RefreshCart !== 'undefined') 
                // We need to disable this rule because the function is a global one
                //eslint-disable-next-line sonarjs/new-cap
                window.RefreshCart();

            refreshActiveMembershipCards(data.MembershipTypeID, MembershipCardStatusEnum.NewCard, data.PriceChangeSuggestions);

        }
        else {
            setErrorMessage(analyseAddToCartErrorMessage(data));
            setAddMembershipCardProcessing(false);
            setShowErrorMessage(true);
        }
    };

    const handleAddMembershipCardToCart = (priceGroupId, member: MembershipTypeSaleInfoModel) => {
        const selectedPriceGroup = member.priceGroups.find((priceGroup) => priceGroup.id === priceGroupId);

        if(!selectedPriceGroup.creditCardCondition)
            addMemberShipCardToCart(member.mainProductEventDateID, selectedPriceGroup.id);
        else {
            setSelectedPriceGroup(selectedPriceGroup);
            setEventDateID(member.mainProductEventDateID);
            showCreditCardCondition();
        }
    };

    const showCreditCardCondition = () => {
        setShowPopupMessageCreditCardCondition(true);
    };

    const addCards = map(membershipTypeInfos, (member: MembershipTypeSaleInfoModel) => {
            const btns = <PriceGroupButtons priceGroups={member.priceGroups} processing={addMembershipCardProcessing} onClick={(priceGroupId) => handleAddMembershipCardToCart(priceGroupId, member)}/>;
            
            return (
                <div key={member.id}>
                    <MarkDown className="markdown-description" source={member.description}></MarkDown>
                    <div className='actions'>
                        <button className="btn btn-link" onClick={function() { window.open(member.becomeMemberUrl); }}>{tr("Plus d'informations...")}</button>
                    </div>
                    {btns}
                </div>
            );
    }); 

    const notMemberCtrl = (
        <div className="col-xs-12">
            <h4>{tr("membership.youarenotamember")}</h4>
            {addCards}
            
            {showErrorMessage && <div className="col-xs-12 alert alert-danger">{errorMessage}</div>}
        </div>
    );
    
    return (
        <div className="MembershipCardPickupControl">
            {showAlreadyMemberSection && <>
                <AlreadyMember 
                    membershipTypeIDs={map(membershipTypeInfos, 'id')}
                    addMembershipCardProcessing={addMembershipCardProcessing}
                    refreshActiveMembershipCards={refreshActiveMembershipCards}
                />
                <div className="row">
                    <div className="col-xs-12">
                        <hr/> 
                    </div>
                </div>
            </>}
            <div className="row">
                {notMemberCtrl}
                <div ref={container} />
                {showPopupMessageCreditCardCondition && eventDateID && <MembershipCardConditionPopup
                    container={container.current}
                    creditCardCondition={selectedPriceGroup.creditCardCondition}
                    onConfirmPaidWithCreditCardCondition={() => addMemberShipCardToCart(eventDateID, selectedPriceGroup.id)}
                    onPopupClose={handlePopupConfirmClose}
                />}
            </div>
        </div>
    );
}
